import { EntityType, validateFile } from '../config/uploads';
import { getPresignedUrl, uploadToS3, deleteFile } from '../api/uploadsApi';
import { UploadResult, UploadProgress } from '../types/uploads';

export class MediaUploader {
  private callApi: (config: any) => Promise<any>;

  constructor(callApi: (config: any) => Promise<any>) {
    this.callApi = callApi;
  }

  /**
   * Upload a file to S3 via presigned URL
   */
  async uploadFile(
    file: File,
    entityType: EntityType,
    entityId: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    // Validate file before upload
    const validation = validateFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Sanitize filename to comply with backend validation rules
    const sanitizeFileName = (name: string): string => {
      // Allow only letters, numbers, dot, underscore, dash; replace others with _
      const cleaned = name.replace(/[^a-zA-Z0-9._-]/g, '_');
      // Ensure maximum length of 255 characters
      return cleaned.slice(0, 255);
    };

    const safeFileName = sanitizeFileName(file.name);

    try {
      // Step 1: Get presigned URL
      const presignedResponse = await getPresignedUrl(this.callApi, {
        entityType,
        entityId,
        fileName: safeFileName,
        contentType: file.type,
      });

      if (!presignedResponse.success) {
        throw new Error('Failed to get presigned URL');
      }

      const { key, uploadUrl } = presignedResponse.data;

      // Step 2: Upload to S3
      await uploadToS3(file, uploadUrl, onProgress);

      // Step 3: Return the S3 key
      return {
        key,
        success: true,
      };
    } catch (error) {
      console.error('Upload failed:', error);
      throw error instanceof Error ? error : new Error('Upload failed');
    }
  }

  /**
   * Delete a file from S3
   */
  async deleteFile(key: string): Promise<void> {
    try {
      await deleteFile(this.callApi, key);
    } catch (error) {
      console.error('Delete failed:', error);
      throw error instanceof Error ? error : new Error('Delete failed');
    }
  }
}

/**
 * Utility function to create a MediaUploader instance
 */
export const createMediaUploader = (callApi: (config: any) => Promise<any>) => {
  return new MediaUploader(callApi);
}; 